export interface PlanarPoint { x: number; y: number }

export function projectPointToSegment(point: PlanarPoint, start: PlanarPoint, end: PlanarPoint) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;
  const fraction = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq));
  const nearest = { x: start.x + fraction * dx, y: start.y + fraction * dy };
  return { fraction, nearest, distanceSq: (point.x - nearest.x) ** 2 + (point.y - nearest.y) ** 2 };
}

export function planarOrientation(a: PlanarPoint, b: PlanarPoint, c: PlanarPoint): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

export function pointOnPlanarSegment(a: PlanarPoint, b: PlanarPoint, point: PlanarPoint, epsilon: number): boolean {
  return Math.abs(planarOrientation(a, b, point)) <= epsilon
    && point.x >= Math.min(a.x, b.x) - epsilon && point.x <= Math.max(a.x, b.x) + epsilon
    && point.y >= Math.min(a.y, b.y) - epsilon && point.y <= Math.max(a.y, b.y) + epsilon;
}

/** Contact policy is explicit: imported polygons permit contact; drawing edits reject it. */
export function planarSegmentsIntersect(a: PlanarPoint, b: PlanarPoint, c: PlanarPoint, d: PlanarPoint, options: { epsilon: number; includeContact: boolean }): boolean {
  const { epsilon, includeContact } = options;
  const abC = planarOrientation(a, b, c), abD = planarOrientation(a, b, d);
  const cdA = planarOrientation(c, d, a), cdB = planarOrientation(c, d, b);
  if (((abC > epsilon && abD < -epsilon) || (abC < -epsilon && abD > epsilon))
    && ((cdA > epsilon && cdB < -epsilon) || (cdA < -epsilon && cdB > epsilon))) return true;
  return includeContact && (pointOnPlanarSegment(a, b, c, epsilon) || pointOnPlanarSegment(a, b, d, epsilon)
    || pointOnPlanarSegment(c, d, a, epsilon) || pointOnPlanarSegment(c, d, b, epsilon));
}
